import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  GreenInfo,
  InputFieldType,
  NLIMicrosoftButton,
  NotLoggedInPage,
  Spinner,
} from '@ydistri/ds';
import * as Sentry from '@sentry/react';
import { MeCollection } from '../../swagger/collections-identity';
import Auth from '../../auth/Auth';
import { setUser } from '../../store/userSlice';
import { Alert } from 'antd';
import { useAppDispatch } from '../../store';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { hasMsalResponseParams } from './hasMsalResponseParams';
import { returnToFromState } from '../../routes/returnTo';

const signInInputs: InputFieldType[] = [
  {
    title: 'Email',
    type: 'text',
    name: 'login',
  },
  {
    title: 'Password',
    type: 'password',
    name: 'password',
  },
];

const auth = new Auth();
const genericSignInError =
  'Sign-in is temporarily unavailable. Please contact support if the issue persists.';

const isFrontendAuthConfigError = (error: Error): boolean =>
  error.message.includes('Missing OAuth client id');

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { search, hash } = location;
  // Back to the guarded page PrivateRoute bounced from, or the root.
  const returnTo = useMemo(() => returnToFromState(location.state), [location.state]);
  const isPopupCallback = window.opener !== null && window.opener !== window;
  const isMsalPopupWindow = window.name.toLowerCase().includes('msal');
  const hasMsalResponse = hasMsalResponseParams(search) || hasMsalResponseParams(hash);
  const shouldHandlePopupCallback = hasMsalResponse || isMsalPopupWindow;
  const activation = parseInt(new URLSearchParams(search).get('activation') ?? '0');
  const passReset = parseInt(new URLSearchParams(search).get('passReset') ?? '0');
  const dispatch = useAppDispatch();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [microsoftSubmitting, setMicrosoftSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  // Capture mount time once; ref avoids re-running Date.now() on re-render.
  const mountTimeRef = useRef<number | undefined>(undefined);
  mountTimeRef.current ??= Date.now();
  const mountTime = mountTimeRef.current;

  useEffect(() => {
    if (!shouldHandlePopupCallback) {
      return;
    }

    broadcastResponseToMainFrame()
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console -- for debugging in case of error
        console.error('Error broadcasting response:', error);
      })
      .finally(() => {
        if (isPopupCallback || isMsalPopupWindow || hasMsalResponse) {
          window.close();
        }
      });
  }, [hasMsalResponse, isPopupCallback, isMsalPopupWindow, shouldHandlePopupCallback]);

  const infoTop = useMemo(
    () =>
      mountTime - (activation + passReset) <= 5000 ? (
        <GreenInfo>Action successful! Please sign in.</GreenInfo>
      ) : null,
    [mountTime, activation, passReset],
  );

  const infoBottom = useMemo(() => {
    return (
      <>
        <Link to="/account/forgot-pass/">Forgot password?</Link>
        {error && <Alert type="error" message={error} />}
      </>
    );
  }, [error]);

  const onSubmit = useCallback(
    (values: Record<string, string>) => {
      setError(undefined);
      setSubmitting(true);
      auth
        .signin(values.login, values.password)
        .then(() => {
          return MeCollection.getMe().then(({ data }) => {
            dispatch(setUser(data.data));
            setSubmitting(false);
            navigate(returnTo);
          });
        })
        .catch((e: unknown) => {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- it is error
          const error = e as Error;
          Sentry.captureException(error, {
            // eslint-disable-next-line @typescript-eslint/naming-convention -- mandated by sentry
            tags: { auth_flow: 'password', app: 'yapp' },
          });

          setError(isFrontendAuthConfigError(error) ? genericSignInError : error.message);
          setSubmitting(false);
        });
    },
    [navigate, dispatch, returnTo],
  );

  const onMicrosoftSubmit = useCallback(() => {
    setError(undefined);
    setMicrosoftSubmitting(true);

    auth
      .signinWithMicrosoft()
      .then(() => {
        return MeCollection.getMe().then(({ data }) => {
          dispatch(setUser(data.data));
          navigate(returnTo);
        });
      })
      .catch((e: unknown) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- it is error
        const error = e as Error;
        Sentry.captureException(error, {
          // eslint-disable-next-line @typescript-eslint/naming-convention -- mandated by sentry
          tags: { auth_flow: 'microsoft', app: 'yapp' },
        });

        setError(isFrontendAuthConfigError(error) ? genericSignInError : error.message);
      })
      .finally(() => {
        setMicrosoftSubmitting(false);
      });
  }, [dispatch, navigate, returnTo]);

  if (shouldHandlePopupCallback) {
    return <Spinner />;
  }

  return (
    <NotLoggedInPage
      title="Sign in to Y′app"
      inputs={signInInputs}
      button="Sign in"
      onSubmit={onSubmit}
      submitting={submitting}
      infoTop={infoTop}
      infoBottom={infoBottom}
    >
      <NLIMicrosoftButton
        onClick={onMicrosoftSubmit}
        disabled={microsoftSubmitting}
        label={microsoftSubmitting ? 'Signing in...' : 'CONTINUE WITH MICROSOFT'}
      />
    </NotLoggedInPage>
  );
};

export default SignIn;
